import TagBox from 'devextreme-react/tag-box';
import type { CSSProperties, ReactNode } from 'react';

export const hvgClientFormLayout: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 14
};

export const hvgClientFormSection: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8
};

export const hvgClientFormSectionTitle: CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  color: 'var(--text-muted, #6b7280)',
  margin: 0,
  paddingBottom: 4,
  borderBottom: '1px solid var(--ops-border, #e5e7eb)'
};

export const hvgClientFormGrid2: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '8px 16px',
  alignItems: 'start'
};

export const hvgClientFormGrid3: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: '8px 12px',
  alignItems: 'start'
};

export const hvgClientFieldLabel: CSSProperties = {
  fontWeight: 600,
  marginBottom: 4,
  fontSize: 12,
  display: 'block',
  color: 'var(--text-primary, #111827)'
};

export const hvgClientFieldHint: CSSProperties = {
  marginTop: 3,
  fontSize: 11,
  color: 'var(--text-muted, #6b7280)',
  lineHeight: 1.35
};

export const hvgClientFormPopupMinWidth = 720;
export const hvgClientFormPopupWidth = 1044;

/** Default popup heights (+45% over prior minimums). */
export const hvgClientUserFormPopupHeight = {
  create: 870,
  edit: 783
} as const;

/** Minimum popup heights — persisted sizes cannot shrink below these. */
export const hvgClientUserFormPopupMinHeight = {
  create: 600,
  edit: 540
} as const;

export function composeDisplayName(givenName: string, surname: string): string {
  return `${givenName.trim()} ${surname.trim()}`.trim();
}

type FormFieldProps = {
  label: string;
  hint?: string;
  span?: 'full';
  children: ReactNode;
};

export function HvgClientFormField({ label, hint, span, children }: FormFieldProps) {
  return (
    <div style={span === 'full' ? { gridColumn: '1 / -1' } : undefined}>
      <label style={hvgClientFieldLabel}>{label}</label>
      {children}
      {hint ? <div style={hvgClientFieldHint}>{hint}</div> : null}
    </div>
  );
}

type MembershipFieldProps = {
  labelSuffix?: string;
  groupItems: { id: string; label: string }[];
  value: string[];
  loading: boolean;
  onChange: (groupIds: string[]) => void;
};

export function HvgClientMembershipField({
  labelSuffix,
  groupItems,
  value,
  loading,
  onChange
}: MembershipFieldProps) {
  return (
    <>
      <label style={hvgClientFieldLabel}>
        Membership{labelSuffix ?? ''}
        {loading ? ' (loading…)' : ''}
      </label>
      {groupItems.length === 0 && !loading ? (
        <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted, #6b7280)' }}>
          No security groups found in this tenant.
        </p>
      ) : (
        <TagBox
          items={groupItems}
          valueExpr="id"
          displayExpr="label"
          value={value}
          searchEnabled
          searchMode="contains"
          searchExpr="label"
          minSearchLength={0}
          showDataBeforeSearch
          hideSelectedItems
          showSelectionControls={false}
          showClearButton
          maxDisplayedTags={4}
          disabled={loading}
          width="100%"
          placeholder="Search groups…"
          noDataText="No groups match your search."
          onValueChanged={(e) => onChange((e.value as string[]) ?? [])}
        />
      )}
    </>
  );
}
